import type { HTMLAttributes } from "react";
import { Suspense, useMemo } from "react";

// Relative, not `@ergon-ai/ui`: the barrel re-exports this file through ./svg,
// so importing the barrel back closes a cycle. In `storybook dev` React Refresh
// then reads the re-exported `Icon` getter while ./svg is still undefined.
import { cn } from "../lib/utils";
import icons from "./icons";

export type IconName = keyof ReturnType<typeof icons>;

interface Props extends HTMLAttributes<HTMLDivElement> {
  name: IconName;
  className?: string;
  rotate?: number;
  color?: string;
}

export const Icon = ({ name, className, rotate, color, ...rest }: Props) => {
  const SvgIcon = useMemo(() => icons()[name], [name]);

  // Disable the lint rule here because we want to handle the case where SvgIcon might be undefined
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!SvgIcon) return null;

  return (
    <div
      aria-label={name}
      role="img"
      className={cn(
        "flex items-center justify-center",
        className,
        rotate ? `rotate-${rotate}` : undefined,
      )}
      {...rest}
    >
      <Suspense fallback={<p>Loading...</p>}>
        <SvgIcon className="h-full w-full" fill={color} fillColor={color} />
      </Suspense>
    </div>
  );
};
